import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc, 
  getDocs, 
  query, 
  where,
  orderBy
} from 'firebase/firestore';
import { db } from '../config/firebase';

const UPGRADE_REQUESTS_COLLECTION = 'upgradeRequests';

export const upgradeRequestService = {
  // Create a new upgrade request
  async createUpgradeRequest(companyId, feature, description = '') {
    try {
      const request = {
        companyId,
        feature, // 'logo_upload', 'advanced_analytics', etc.
        description,
        status: 'pending', // pending, approved, rejected
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const docRef = await addDoc(collection(db, UPGRADE_REQUESTS_COLLECTION), request);
      return { id: docRef.id, ...request };
    } catch (error) {
      throw error;
    }
  },

  // Get upgrade requests by company
  async getUpgradeRequestsByCompany(companyId) {
    try {
      const q = query(
        collection(db, UPGRADE_REQUESTS_COLLECTION),
        where('companyId', '==', companyId)
      );
      const querySnapshot = await getDocs(q);
      const requests = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      return requests.sort((a, b) => b.createdAt?.toDate?.() - a.createdAt?.toDate?.());
    } catch (error) {
      throw error;
    }
  },

  // Get all upgrade requests (for admin)
  async getAllUpgradeRequests() {
    try {
      const q = query(
        collection(db, UPGRADE_REQUESTS_COLLECTION),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const requests = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      return requests;
    } catch (error) {
      throw error;
    }
  },

  // Get upgrade request by ID
  async getUpgradeRequestById(requestId) {
    try {
      const docRef = doc(db, UPGRADE_REQUESTS_COLLECTION, requestId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      } else {
        throw new Error('Upgrade request not found');
      }
    } catch (error) {
      throw error;
    }
  },

  // Update upgrade request status (approve/reject)
  async updateUpgradeRequestStatus(requestId, status, adminNotes = '') {
    try {
      const docRef = doc(db, UPGRADE_REQUESTS_COLLECTION, requestId);
      await updateDoc(docRef, {
        status,
        adminNotes,
        updatedAt: new Date()
      });
      return { id: requestId, status, adminNotes };
    } catch (error) {
      throw error;
    }
  },

  // Check if company has pending request for a feature
  async hasPendingRequest(companyId, feature) {
    try {
      const q = query(
        collection(db, UPGRADE_REQUESTS_COLLECTION),
        where('companyId', '==', companyId),
        where('feature', '==', feature),
        where('status', '==', 'pending')
      );
      const querySnapshot = await getDocs(q);
      return !querySnapshot.empty;
    } catch (error) {
      throw error;
    }
  },

  // Check if company has approved request for a feature
  async hasApprovedRequest(companyId, feature) {
    try {
      const q = query(
        collection(db, UPGRADE_REQUESTS_COLLECTION),
        where('companyId', '==', companyId),
        where('feature', '==', feature),
        where('status', '==', 'approved')
      );
      const querySnapshot = await getDocs(q);
      return !querySnapshot.empty;
    } catch (error) {
      throw error;
    }
  },

  // Delete upgrade request
  async deleteUpgradeRequest(requestId) {
    try {
      const docRef = doc(db, UPGRADE_REQUESTS_COLLECTION, requestId);
      await deleteDoc(docRef);
      return true;
    } catch (error) {
      throw error;
    }
  }
}; 